import * as Sentry from '@sentry/react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { NPIAddress, NPIResult, NPITaxonomy, useNPILookup } from '../hooks/useNPILookup'

/** US ZIP: show at most the first 5 digits (NPPES often returns ZIP+4 without hyphen). */
function formatPostalCodeFiveDigits(postalCode: string | undefined): string {
  if (!postalCode?.trim()) return ''
  const digits = postalCode.replace(/\D/g, '')
  if (digits.length >= 5) return digits.slice(0, 5)
  return postalCode.trim()
}

function formatMailingAddressLine(addresses: NPIAddress[] | undefined): string | null {
  const addr =
    addresses?.find((a) => a.address_purpose === 'MAILING') ?? addresses?.[0]
  if (!addr) return null
  const street = [addr.address_1, addr.address_2].filter(Boolean).join(', ')
  const zip = formatPostalCodeFiveDigits(addr.postal_code)
  return [street, `${addr.city}, ${addr.state} ${zip}`.trim()].filter(Boolean).join(' · ')
}

function taxonomyKey(tax: NPITaxonomy, index: number): string {
  return `${tax.code}-${tax.state}-${tax.license ?? ''}-${index}`
}

interface NPILookupModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (result: NPIResult) => void
  /** Seed the search box with this value each time the modal opens (e.g. the NPI already typed in the form). */
  initialQuery?: string
}

/**
 * NPI Lookup Modal with type-ahead search
 * Implements design from Figma (node-id=21-4608)
 * @param {NPILookupModalProps} props - Modal props
 * @returns {JSX.Element} The NPI lookup modal component
 */
const NPILookupModal: React.FC<NPILookupModalProps> = ({ isOpen, onClose, onSelect, initialQuery }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const { results, isSearching, error, search, clearResults } = useNPILookup()
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Seed the search box with the value already typed into the form (e.g. the NPI
  // the admin entered) whenever the modal opens, so the lookup prepopulates it.
  useEffect(() => {
    if (isOpen) {
      setSearchQuery(initialQuery?.trim() ?? '')
    }
  }, [isOpen, initialQuery])

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Debounced search
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        search(searchQuery)
      } else {
        clearResults()
      }
    }, 300)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [searchQuery, search, clearResults])

  const handleSelect = useCallback(
    (result: NPIResult) => {
      Sentry.startSpan(
        {
          op: 'ui.click',
          name: 'NPI Lookup - Select Result',
        },
        () => {
          onSelect(result)
          onClose()
          setSearchQuery('')
          clearResults()
        }
      )
    },
    [onSelect, onClose, clearResults]
  )

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return

      switch (e.key) {
        case 'Escape':
          onClose()
          break
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
          break
        case 'Enter':
          e.preventDefault()
          if (selectedIndex >= 0 && results[selectedIndex]) {
            handleSelect(results[selectedIndex])
          }
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, results, selectedIndex, onClose, handleSelect])

  const handleBack = () => {
    Sentry.startSpan(
      {
        op: 'ui.click',
        name: 'NPI Lookup - Close Modal',
      },
      () => {
        onClose()
        setSearchQuery('')
        clearResults()
      }
    )
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="relative w-full max-w-2xl rounded-base border border-border-base bg-white p-6 shadow-[0px_25px_50px_0px_rgba(29,41,61,0.25)]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <h2 className="mb-6 text-2xl font-semibold leading-8 tracking-tight text-text-heading">NPI Lookup</h2>

          {/* Search Input */}
          <div className="mb-6 flex items-center gap-2">
            {/* Back Button */}
            <button
              type="button"
              onClick={handleBack}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-base text-text-body transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
              aria-label="Go back"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Search Input */}
            <div className="flex flex-1 items-center gap-2 rounded-base border border-brand-600 bg-[#fafafa] px-4 py-3.5 shadow-xs">
              <svg
                className="h-6 w-6 shrink-0 text-text-body-subtle"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 border-0 bg-transparent p-0 text-base leading-6 text-text-heading placeholder:text-text-body-subtle focus:outline-none focus:ring-0"
                placeholder="Search by number or name"
              />
            </div>
          </div>

          {/* Content Area */}
          <div className="mb-6">
            {!searchQuery || searchQuery.trim().length < 2 ? (
              /* Empty State */
              <div className="flex min-h-[300px] flex-col items-center justify-center py-8">
                <svg
                  className="mb-2 h-6 w-6 text-[#99a1af]"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="text-lg font-semibold leading-5 tracking-tight text-[#99a1af]">Start typing to search</p>
              </div>
            ) : isSearching ? (
              /* Loading State */
              <div className="flex min-h-[300px] flex-col items-center justify-center py-8">
                <svg
                  className="h-8 w-8 animate-spin text-brand-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <p className="mt-2 text-sm text-text-body-subtle">Searching...</p>
              </div>
            ) : error ? (
              /* Error State */
              <div className="flex min-h-[300px] flex-col items-center justify-center py-8">
                <p className="text-sm text-error-600">{error}</p>
              </div>
            ) : results.length === 0 ? (
              /* No Results */
              <div className="flex min-h-[300px] flex-col items-center justify-center py-8">
                <p className="text-sm text-text-body-subtle">No results found</p>
              </div>
            ) : (
              /* Results Section */
              <div className="flex flex-col gap-3">
                {/* Results Header */}
                <div className="flex items-start justify-between leading-5 text-text-body-subtle">
                  <p className="text-lg font-semibold tracking-tight">Results for &quot;{searchQuery}&quot;</p>
                  <p className="text-xs font-medium">
                    50 most relevant results shown. Add more characters to narrow your search.
                  </p>
                </div>

                {/* Separator */}
                <div className="border-t border-border-base" />

                {/* Results List */}
                <div className="flex max-h-[400px] flex-col overflow-y-auto">
                  {results.map((result, index) => {
                    const mailingAddressLine = formatMailingAddressLine(result.addresses)
                    return (
                      <button
                        key={result.npiNumber}
                        type="button"
                        onClick={() => handleSelect(result)}
                        className={`flex items-start justify-between gap-4 p-4 text-left transition-all dark:hover:bg-gray-800/50 ${
                          index === selectedIndex
                            ? 'rounded-base border border-[#e9d4ff] bg-[#faf5ff] dark:border-purple-900/60 dark:bg-purple-950/40'
                            : 'rounded-base hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                          <p
                            className={`text-base font-medium leading-5 ${index === selectedIndex ? 'text-[#59168b] dark:text-purple-300' : 'text-text-heading dark:text-white'}`}
                          >
                            {result.firstName} {result.lastName}
                          </p>
                          <p
                            className={`font-mono text-sm leading-5 ${index === selectedIndex ? 'text-[#59168b] dark:text-purple-300' : 'text-text-body-subtle dark:text-gray-400'}`}
                          >
                            {result.npiNumber}
                          </p>
                          {mailingAddressLine && (
                            <p
                              className={`text-xs leading-snug ${index === selectedIndex ? 'text-[#59168b]/90 dark:text-purple-200/90' : 'text-text-body dark:text-gray-400'}`}
                            >
                              {mailingAddressLine}
                            </p>
                          )}
                          {result.taxonomies && result.taxonomies.length > 0 && (
                            <ul className="mt-0.5 space-y-1">
                              {result.taxonomies.map((tax, taxIndex) => (
                                <li
                                  key={taxonomyKey(tax, taxIndex)}
                                  className={`text-xs font-medium leading-snug ${index === selectedIndex ? 'text-[#59168b]/95 dark:text-purple-200/95' : 'text-text-body dark:text-gray-300'}`}
                                >
                                  {tax.desc}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        {index === selectedIndex && (
                          <div className="flex shrink-0 items-center gap-2 pt-0.5">
                            <div className="flex w-[73px] items-center gap-2 rounded-2xl border border-border-base-medium bg-[#f4f4f5] px-2 py-1.5 dark:border-gray-600 dark:bg-gray-800">
                              <svg className="h-[11px] w-[13px]" fill="currentColor" viewBox="0 0 13 11">
                                <path d="M12 0v6.5H3l2-2-1-1-4 4 4 4 1-1-2-2h10V0h-1z" />
                              </svg>
                              <span className="text-xs leading-4 text-text-heading dark:text-gray-200">Enter</span>
                            </div>
                            <span className="text-xs leading-5 text-text-body-subtle dark:text-gray-400">to add</span>
                          </div>
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-border-base pt-6">
            {/* Keyboard Hints */}
            <div className="flex flex-1 items-center gap-4">
              {/* Navigate */}
              <div className="flex items-center gap-2">
                <div className="flex h-[30px] w-[32px] items-center justify-center rounded-2xl border border-border-base-medium bg-[#f4f4f5] px-2 py-1.5">
                  <svg className="h-[7.2px] w-3" fill="currentColor" viewBox="0 0 12 8">
                    <path d="M6 0L12 7.2H0L6 0Z" />
                  </svg>
                </div>
                <div className="flex h-[30px] w-[32px] items-center justify-center rounded-2xl border border-border-base-medium bg-[#f4f4f5] px-2 py-1.5">
                  <svg className="h-[7.2px] w-3" fill="currentColor" viewBox="0 0 12 8">
                    <path d="M6 7.2L0 0H12L6 7.2Z" />
                  </svg>
                </div>
                <span className="text-xs leading-5 text-text-body-subtle">Navigate</span>
              </div>

              {/* Enter */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 rounded-2xl border border-border-base-medium bg-[#f4f4f5] px-2 py-1.5">
                  <svg className="h-[11px] w-[13px]" fill="currentColor" viewBox="0 0 13 11">
                    <path d="M12 0v6.5H3l2-2-1-1-4 4 4 4 1-1-2-2h10V0h-1z" />
                  </svg>
                  <span className="text-xs leading-4 text-text-heading">Enter</span>
                </div>
                <span className="text-xs leading-5 text-text-body-subtle">Select</span>
              </div>

              {/* Esc */}
              <div className="flex items-center gap-2">
                <div className="rounded-2xl border border-border-base-medium bg-[#f4f4f5] px-2 py-1.5">
                  <span className="text-xs leading-4 text-text-heading">Esc</span>
                </div>
                <span className="text-xs leading-5 text-text-body-subtle">Close</span>
              </div>
            </div>

            {/* Done Button */}
            <button
              type="button"
              onClick={onClose}
              className="rounded-base bg-brand-600 px-4 py-2.5 text-sm font-medium leading-5 text-white shadow-xs transition-colors hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default NPILookupModal
